import { useEffect, useRef } from 'react';

export interface HotkeySpec {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

/**
 * "mod+k" (Cmd on macOS, Ctrl elsewhere), "shift+/" , "?" , "alt+n", "Escape".
 * A bare printable key such as "?" ignores shift (the layout decides whether it needs it).
 */
export function parseHotkey(spec: string): HotkeySpec {
  const parts = spec.split('+').map((p) => p.trim());
  const key = (parts.pop() ?? '').toLowerCase();
  const mods = parts.map((p) => p.toLowerCase());
  return { key, mod: mods.includes('mod') || mods.includes('ctrl') || mods.includes('cmd'), shift: mods.includes('shift'), alt: mods.includes('alt') };
}

export function matchesHotkey(event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>, spec: HotkeySpec): boolean {
  if (event.key.toLowerCase() !== spec.key) {
    return false;
  }
  const mod = event.metaKey || event.ctrlKey;
  if (mod !== spec.mod || event.altKey !== spec.alt) {
    return false;
  }
  const printable = spec.key.length === 1 && !/[a-z0-9]/.test(spec.key);
  return printable ? true : event.shiftKey === spec.shift;
}

/** True when the event target is somewhere the user is typing. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export interface HotkeyOptions {
  /** Fire even while the user is typing in a field (default: only for combos with a modifier). */
  allowInInput?: boolean;
  enabled?: boolean;
  preventDefault?: boolean;
}

/** Runs `handler` on a global keyboard shortcut, e.g. useHotkey('mod+k', open). */
export function useHotkey(spec: string, handler: (event: KeyboardEvent) => void, options: HotkeyOptions = {}): void {
  const { allowInInput, enabled = true, preventDefault = true } = options;
  const saved = useRef(handler);
  useEffect(() => {
    saved.current = handler;
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const parsed = parseHotkey(spec);
    const onKey = (event: KeyboardEvent): void => {
      if (!matchesHotkey(event, parsed)) {
        return;
      }
      if (!parsed.mod && !allowInInput && isTypingTarget(event.target)) {
        return;
      }
      if (preventDefault) {
        event.preventDefault();
      }
      saved.current(event);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [spec, enabled, allowInInput, preventDefault]);
}

/**
 * Vim-style two-key sequence ("g" then "d") with a short timeout, ignored while typing.
 * Returns nothing; the handler fires on completion.
 */
export function useHotkeySequence(first: string, second: string, handler: () => void, timeoutMs = 900): void {
  const saved = useRef(handler);
  useEffect(() => {
    saved.current = handler;
  });
  useEffect(() => {
    let armedAt = 0;
    const onKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
        return;
      }
      const key = event.key.toLowerCase();
      const now = Date.now();
      if (armedAt && now - armedAt <= timeoutMs && key === second.toLowerCase()) {
        armedAt = 0;
        saved.current();
        return;
      }
      armedAt = key === first.toLowerCase() ? now : 0;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [first, second, timeoutMs]);
}

export const isMac = (): boolean => typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);
/** Label for the platform's primary modifier key. */
export const modKeyLabel = (): string => (isMac() ? '⌘' : 'Ctrl');
